import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import { LeadLogContactModal, TierBadge, ScoreBar, ContactMethodBadge } from './LeadContacts';
import MemberTimeline from '../t2m/MemberTimeline';
import ContactLines from '../../components/ContactLines';
import SuppressionBadges from '../../components/SuppressionBadges';

// ─── History side panel (Sprint 21.3, 9/26/26) ───────────────────────────────
// BK: "if it shows 'Contacted 8-11', can we see the history as a side view
// so we know the context?" — reuses the shared Member Timeline (built this
// session for the CLAUDE.md universal-visibility rule) rather than a new,
// Lead-Engine-only history view, so this shows contacts from every module
// (Retention/T2M/PB101), not just Lead Engine's own.
function HistorySidePanel({ card, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 380, maxWidth: '100%', height: '100%', overflowY: 'auto',
          background: '#141414', borderLeft: '1px solid #2a2a2a', padding: '16px 18px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{card.name}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <MemberTimeline memberId={card.memberId} memberName={card.name} />
      </div>
    </div>
  );
}

// ─── Goal banner (Sprint 21.3, 9/25/26) ──────────────────────────────────────
// Front-and-center per BK: viewed by Inside Sales, set by the rep themselves
// in their own view — but only during an Admin-granted unlock window, same
// model as commission-sale editing (edit_unlocked_until, self-expiring,
// default 2h). Admin can also set any rep's goal directly, always. Workflow
// BK described: goals get set at the end of the month, for the next month.
function monthLabel(iso) {
  return new Date(iso + '-01T12:00:00').toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
function nextMonthISO() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function GoalSetForm({ repUserId, existingAmount, onSaved, post }) {
  const [amount, setAmount] = useState(existingAmount || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const goalMonth = nextMonthISO();

  async function handleSave() {
    if (!amount || Number(amount) <= 0) { setError('Enter a real number'); return; }
    setSaving(true);
    setError('');
    try {
      await post('/lead/goal', { repUserId, goalMonth, goalAmount: Number(amount) });
      onSaved();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
      <span style={{ fontSize: 10.5, color: '#888' }}>Set {monthLabel(goalMonth.slice(0, 7))} goal:</span>
      <input
        type="number" value={amount} onChange={e => setAmount(e.target.value)}
        placeholder="e.g. 200"
        style={{ width: 80, background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '4px 6px', fontSize: 12 }}
      />
      <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} disabled={saving} onClick={handleSave}>
        {saving ? 'Saving...' : 'Save'}
      </button>
      {error && <span style={{ fontSize: 10.5, color: '#ef4444' }}>{error}</span>}
    </div>
  );
}

// Condensed 9/25/26 (BK: "way too much page real estate for the KPI") —
// this used to be a tall standalone banner; now a compact stat matching
// FollowUpKPI/ContactsKPI's own small footprint, with all the admin
// controls (rep picker, set-goal form, unlock button) collapsed behind a
// single "Manage" toggle instead of always taking up vertical space.
function statBoxStyle() {
  return { background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7, padding: '6px 10px', minWidth: 150 };
}

function LeadGoalStat() {
  const { get, post } = useApi();
  const { isAdmin } = useAuth();
  const [reps, setReps] = useState([]);
  const [repUserId, setRepUserId] = useState(null);
  const [goalData, setGoalData] = useState(null);
  const [contactsKpi, setContactsKpi] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      get('/users?role=sales').then(res => {
        const list = res?.users || [];
        setReps(list);
        if (list.length > 0) setRepUserId(list[0].id);
      }).catch(() => {});
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    const url = repUserId ? `/lead/goal?repUserId=${repUserId}` : '/lead/goal';
    get(url).then(setGoalData).catch(() => setGoalData(null));
    const kpiUrl = repUserId
      ? `/lead/kpi/contacts?granularity=month&repUserId=${repUserId}`
      : '/lead/kpi/contacts?granularity=month';
    get(kpiUrl).then(setContactsKpi).catch(() => setContactsKpi(null));
  }, [repUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleUnlock() {
    if (!goalData?.repUserId) return;
    setUnlocking(true);
    try {
      await post('/lead/goal/unlock', { repUserId: goalData.repUserId, hours: 2 });
      load();
    } catch (e) { /* surfaced via unchanged isUnlocked state */ }
    finally { setUnlocking(false); }
  }

  if (!goalData) return null;

  const thisMonth = goalData.goals.find(g => String(g.goalMonth).slice(0, 7) === currentMonthKey());
  const nextMonth = goalData.goals.find(g => String(g.goalMonth).slice(0, 7) === nextMonthISO().slice(0, 7));
  const thisMonthRollup = (contactsKpi?.rollup || []).filter(r => String(r.bucket).slice(0, 7) === currentMonthKey());
  const actualCount = thisMonthRollup.reduce((s, r) => s + r.count, 0);
  const goalAmount = thisMonth?.goalAmount || null;
  const pacePct = goalAmount ? Math.round((actualCount / goalAmount) * 100) : null;
  const color = pacePct === null ? '#888' : pacePct >= 100 ? '#22c55e' : pacePct >= 70 ? '#f59e0b' : '#ef4444';

  const canManage = isAdmin || goalData.isUnlocked;

  return (
    <div style={statBoxStyle()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Contacted — {monthLabel(currentMonthKey())}
        </div>
        {canManage && (
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 9, padding: '1px 4px' }} onClick={() => setManageOpen(o => !o)}>
            {manageOpen ? '✕' : '⚙'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: goalAmount ? color : '#ccc' }}>
        {actualCount}{goalAmount ? ` / ${goalAmount}` : ''}
      </div>
      <div style={{ fontSize: 9, color: '#666' }}>
        {pacePct !== null ? `${pacePct}% of pace` : 'No goal set'}
      </div>

      {manageOpen && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #2a2a2a' }}>
          {isAdmin && reps.length > 1 && (
            <select
              value={repUserId || ''} onChange={e => setRepUserId(e.target.value)}
              style={{ width: '100%', marginBottom: 4, background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '3px 5px', fontSize: 11 }}
            >
              {reps.map(r => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
            </select>
          )}
          <div style={{ fontSize: 9.5, color: '#666', marginBottom: 4 }}>
            {nextMonth ? `${monthLabel(nextMonthISO().slice(0, 7))}: ${nextMonth.goalAmount}` : `${monthLabel(nextMonthISO().slice(0, 7))} not set`}
          </div>
          {(isAdmin || goalData.isUnlocked) && (
            <GoalSetForm repUserId={goalData.repUserId} existingAmount={nextMonth?.goalAmount} onSaved={load} post={post} />
          )}
          {isAdmin && !goalData.isUnlocked && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 9.5, marginTop: 4 }} disabled={unlocking} onClick={handleUnlock}>
              {unlocking ? 'Unlocking...' : `Unlock for ${reps.find(r => r.id === repUserId)?.name || 'rep'} (2h)`}
            </button>
          )}
          {!isAdmin && !goalData.isUnlocked && (
            <div style={{ fontSize: 9.5, color: '#888', fontStyle: 'italic' }}>Ask an Admin to unlock this.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Lead Engine Kanban (Sprint 21.3, 9/25/26) ───────────────────────────────
// Per docs/COMS_LeadEngine_Kanban_Design_Decisions_2026-09-25.md — same
// design language as PB101's/Retention's boards. Reads GET /lead/kanban
// (getLeadEngineKanbanBoard, queries.js) and reuses LeadLogContactModal
// (LeadContacts.jsx) for logging outcomes, rather than the broken
// LeadOutcomeModal path (My Leads/Prospect Detail silently dropped
// follow-up scheduling — see design doc). No club-side ownership track —
// Lead Engine is Inside-Sales-only (canSales gate), so unlike PB101/
// Retention there's no color-coded owner header to show.

const STAGE_META = {
  new:        { label: 'New' },
  working:    { label: 'Working' },
  followUp:   { label: 'Follow Up Scheduled', desc: 'A follow-up is on the calendar. Cards stay here until the next contact is logged; red once the date has passed.' },
  trialReady: { label: 'Trial Ready', desc: 'No membership or trial history yet (new inbound lead, new PbP profile). Offer a trial.' },
  promo:      { label: 'Promo', desc: 'Membership or trial ended in the last 30 days. Lead with a discount offer; the badge shows the plan they just left.' },
  winBack:    { label: 'Win Back', desc: 'Past members and past trialists. Use your judgement: a trial can be offered from here.' },
  neverMember: { label: 'Played, Never Joined', desc: 'Played with us but never held a membership (mostly one-time drop-ins). A different pitch than a past member: a first trial or a membership.' },
  nurture:    { label: 'Nurture / Cool Period' },
  won:        { label: 'Won' },
  lost:       { label: 'Lost' },
};
const COLUMN_ORDER = ['new', 'working', 'followUp', 'trialReady', 'promo', 'winBack', 'neverMember', 'nurture', 'won', 'lost'];

function Badge({ children, color }) {
  return (
    <span style={{
      background: `${color}22`, color, border: `1px solid ${color}`,
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4, display: 'inline-block', marginBottom: 4,
    }}>
      {children}
    </span>
  );
}

function FollowUpBadge({ card }) {
  if (!card.followUpDate) return null;
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const color = card.followUpDate <= today ? '#ef4444' : card.followUpDate === tomorrow ? '#f59e0b' : '#3b82f6';
  const label = new Date(card.followUpDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  return <Badge color={color}>📅 Follow up {label}</Badge>;
}

// Card redesign (BK, 9/25/26): "the current LE card has a lot more
// informative info on it, I was expecting to keep that info" — reusing the
// actual sub-components the old Today's Contacts/Win Back cards were built
// from (TierBadge/ContactMethodBadge/ScoreBar, LeadContacts.jsx) rather than
// reimplementing a thinner version, so this stays visually/behaviorally
// consistent instead of drifting. Dropped the old PipelineBadge specifically
// (BK: "some of the info is now in the form of the stage itself" — the
// Kanban's own column already tells you the pipeline, no need to repeat it
// on every card). Whole card is clickable to Log Outcome (BK: "easier to
// click on the card itself"), not just a button at the bottom.
function KanbanCard({ card, stageKey, onLogOutcome, onViewHistory, resumeId }) {
  const isTerminal = stageKey === 'won' || stageKey === 'lost';
  return (
    <div
      data-card-id={card.id}
      onClick={!isTerminal ? () => onLogOutcome(card, stageKey) : undefined}
      style={{
        background: '#141414', border: card.id === resumeId ? '2px solid #60a5fa' : '1px solid #2a2a2a', borderRadius: 7,
        padding: '8px 9px', marginBottom: 6,
        cursor: isTerminal ? 'default' : 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 2 }}>
        <span style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {card.name}
        </span>
        <TierBadge tier={card.tier} />
        <ContactMethodBadge pipeline={card.pipeline} />
      </div>

      <ContactLines phone={card.phone} email={card.email} name={card.name} clubId={card.clubId} />
      <SuppressionBadges doNotText={card.doNotText} doNotEmail={card.doNotEmail} />
      {!card.phone && !card.email && (
        <div style={{ marginTop: 4 }}><Badge color="#6b7280">No phone or email on file</Badge></div>
      )}

      {(card.isPastMember || card.visits30d > 0 || card.skillBand !== 'Not Rated') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
          {card.isPastMember && (
            <Badge color="#60a5fa">💙 Past Member{card.membershipType ? ` · ${card.membershipType}` : ''}</Badge>
          )}
          {card.visits30d > 0 && (
            <Badge color={card.visits30d >= 4 ? '#22c55e' : card.visits30d >= 2 ? '#eab308' : '#9ca3af'}>
              {card.visits30d}x last 30d
            </Badge>
          )}
          {card.skillBand !== 'Not Rated' && (
            <Badge color="#c084fc">
              {card.skillBand}{card.skillBandSource === 'program' ? ' (recent play)' : ''}
            </Badge>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 2 }}>
        {(stageKey === 'winBack' || stageKey === 'neverMember') && (
          <Badge color="#a855f7">{card.isPastMember ? 'Past Member' : 'Non-Member'}</Badge>
        )}
        {(stageKey === 'winBack' || stageKey === 'neverMember') && card.daysInactive !== null && card.daysInactive !== undefined && (
          <Badge color="#6b7280">{card.daysInactive}d inactive</Badge>
        )}
        {(stageKey === 'winBack' || stageKey === 'neverMember') && card.discountEligible && (
          <Badge color="#22c55e">🎾 Still visiting — 20% offer</Badge>
        )}
        {(stageKey === 'winBack' || stageKey === 'neverMember') && card.winBackOfferEligible && (
          <Badge color="#3b82f6">💙 Buy 2 Get 1 target</Badge>
        )}
        {stageKey === 'promo' && card.promo && (
          <>
            <Badge color="#f97316">{card.promo.lastPlan}</Badge>
            <Badge color="#6b7280">ended {card.promo.daysSinceEnded}d ago</Badge>
          </>
        )}
        {stageKey === 'nurture' && card.latestOutcome && (
          <Badge color="#eab308">{card.latestOutcome}</Badge>
        )}
        <FollowUpBadge card={card} />
      </div>

      {card.latestContactAt && (
        <div
          onClick={e => { e.stopPropagation(); onViewHistory(card); }}
          title="View full contact history"
          style={{ fontSize: 9, color: '#a78bfa', marginBottom: 2, textDecoration: 'underline', textDecorationStyle: 'dotted', width: 'fit-content' }}
        >
          ✓ Contacted {new Date(card.latestContactAt).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
        </div>
      )}

      <ScoreBar score={card.score} tier={card.tier} />
    </div>
  );
}

function Column({ stageKey, cards, onLogOutcome, onViewHistory, resumeId }) {
  const meta = STAGE_META[stageKey];
  return (
    <div style={{ flex: '0 0 168px', width: 168 }}>
      <div style={{ background: '#1c1c1c', borderRadius: 7, padding: '5px 7px', marginBottom: 6 }}>
        <span style={{
          float: 'right', fontFamily: 'monospace', fontSize: 10, fontWeight: 500,
          background: '#0a0a0a', color: '#a8a496', padding: '1px 6px', borderRadius: 20,
        }}>{cards.length}</span>
        <div style={{ fontWeight: 700, fontSize: 10.5, color: '#a8a496', lineHeight: 1.3 }}>{COLUMN_ORDER.indexOf(stageKey) + 1} · {meta.label}</div>
        {meta.desc && <div style={{ fontSize: 9, color: '#888', marginTop: 4, lineHeight: 1.35 }}>{meta.desc}</div>}
      </div>
      {cards.length === 0 && (
        <div style={{ fontSize: 10, color: '#555', textAlign: 'center', padding: '12px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>
          Nothing here
        </div>
      )}
      {cards.map(c => (
        <KanbanCard key={c.id} card={c} stageKey={stageKey} onLogOutcome={onLogOutcome} onViewHistory={onViewHistory} resumeId={resumeId} />
      ))}
    </div>
  );
}

// ─── KPI header strip ─────────────────────────────────────────────────────────
function FollowUpKPI({ get }) {
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    get('/lead/kpi/followups').then(setStats).catch(() => setStats(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stats) return null;
  const color = stats.pctOnTime === null ? '#888' : stats.pctOnTime >= 80 ? '#22c55e' : stats.pctOnTime >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div style={statBoxStyle()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Follow-Ups On Time
        </div>
        {stats.byRep.length > 1 && (
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 9, padding: '1px 4px' }}
            onClick={() => setExpanded(e => !e)}>
            {expanded ? '✕' : 'by rep'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>
        {stats.pctOnTime === null ? '—' : `${stats.pctOnTime}%`}
      </div>
      <div style={{ fontSize: 9, color: '#666' }}>
        {stats.onTime} on time · {stats.late} late · {stats.missed} missed
      </div>
      {expanded && (
        <div style={{ marginTop: 6 }}>
          {stats.byRep.map(r => (
            <div key={r.repUserId} style={{ fontSize: 9.5, color: '#aaa', marginBottom: 2 }}>
              {r.repName}: {r.pctOnTime === null ? '—' : `${r.pctOnTime}%`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const GRANULARITIES = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

// Real current period key, matching how the backend truncates
// (DATE_TRUNC('day'|'week'|'month', ol.logged_at)) — 'week' is Monday-start
// (Postgres default ISO week). Bug found 9/25/26 (BK, screenshot showing an
// unrelated-looking number with no date attached): this used to just grab
// the LAST bucket the query happened to return, which is the most recent
// day/week/month with ANY logged contact — not necessarily today/this
// week/this month, and gave no indication which period it actually was.
// Now always shows the real current period, defaulting to 0 if nothing's
// been logged yet.
function currentBucketKey(granularity) {
  const now = new Date();
  if (granularity === 'month') {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  if (granularity === 'week') {
    const day = now.getDay(); // 0=Sun..6=Sat
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    return monday.toISOString().slice(0, 10);
  }
  return now.toISOString().slice(0, 10);
}
function bucketMatchesKey(bucket, granularity, key) {
  const s = String(bucket);
  return granularity === 'month' ? s.slice(0, 7) === key : s.slice(0, 10) === key;
}

function ContactsKPI({ get }) {
  const [granularity, setGranularity] = useState('day');
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    get(`/lead/kpi/contacts?granularity=${granularity}`).then(setKpi).catch(() => setKpi(null));
  }, [granularity]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!kpi) return null;
  const currentKey = currentBucketKey(granularity);
  const currentRows = kpi.rollup.filter(r => bucketMatchesKey(r.bucket, granularity, currentKey));
  const totalCount = currentRows.reduce((sum, r) => sum + r.count, 0);
  const totalGoal = currentRows.reduce((sum, r) => sum + (r.paceGoal || 0), 0);
  const hasGoal = currentRows.some(r => r.paceGoal);
  const pacePct = hasGoal && totalGoal > 0 ? Math.round((totalCount / totalGoal) * 100) : null;
  const color = pacePct === null ? '#888' : pacePct >= 100 ? '#22c55e' : pacePct >= 70 ? '#f59e0b' : '#ef4444';

  return (
    <div style={statBoxStyle()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Contacts
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          {GRANULARITIES.map(g => (
            <button key={g.key} onClick={() => setGranularity(g.key)}
              style={{
                fontSize: 8, padding: '1px 4px', borderRadius: 3, cursor: 'pointer',
                border: `1px solid ${granularity === g.key ? '#3b82f6' : '#333'}`,
                background: granularity === g.key ? '#3b82f622' : 'transparent',
                color: granularity === g.key ? '#3b82f6' : '#888',
              }}>
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: hasGoal ? color : '#ccc' }}>
        {totalCount}{hasGoal ? ` / ${Math.round(totalGoal)}` : ''}
      </div>
      <div style={{ fontSize: 9, color: '#666' }}>
        {pacePct !== null ? `${pacePct}% of pace` : `No goal — ${granularity}`}
      </div>
    </div>
  );
}

// Work-down-the-list flow (Josh, 10/17/26: "pick back up at lead 322"). Two causes of losing your place before:
// saving reloaded the board behind a full-page spinner (scroll reset to the top), and nothing remembered which card
// was next. Now: (1) saving reloads quietly, so the page stays where it is and the saved card just leaves the column;
// (2) the modal can step to the next card (Next, or automatically after saving when the toggle is on); (3) the last
// card worked is remembered per club and highlighted, with a Resume button that scrolls back to it.
const AUTO_NEXT_KEY = 'leadKanbanAutoNext';
const lastKey = (clubId) => `leadKanbanLast:${clubId}`;
const readStore = (k, fallback) => { try { const v = window.localStorage.getItem(k); return v === null ? fallback : v; } catch { return fallback; } };
const writeStore = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* storage may be blocked */ } };

function scrollToCard(id) {
  window.requestAnimationFrame(() => {
    const el = document.querySelector(`[data-card-id="${id}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
}

export default function LeadEngineKanban() {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [autoNext, setAutoNext] = useState(() => readStore(AUTO_NEXT_KEY, 'off') === 'on');   // optional; off by default (BK: keeping your place matters more)
  const [resumeId, setResumeId] = useState(null);
  const [toast, setToast] = useState('');
  const queueRef = useRef(null);   // { stageKey, ids } in the order the column was showing when the card was opened
  const scrolledToResume = useRef(null);   // club id we already scrolled back to the remembered card for

  // Bug found 9/25/26 (BK: "should these have contacts in the stages?" —
  // board showed 0 across every stage for McKinney): this used to send
  // club=ALL for admin/mloc, but getLeadEngineKanbanBoard treats a real
  // string as a literal location_id filter, not a sentinel — 'ALL' just
  // matched nothing. There's no backend-side 'ALL' handling here (unlike
  // /lead/followups, which does parse that sentinel), and there doesn't
  // need to be — the header club selector already lets admin switch clubs,
  // same as every other board (PB101Kanban, RetentionKanban). Just send
  // whichever club is actually selected.
  // silent = refresh the data without the full-page spinner (keeps scroll position) and without
  // replacing the board with an error screen if the refresh fails.
  const load = useCallback((silent = false) => {
    if (!currentClub?.id) { setLoading(false); return Promise.resolve(null); }
    if (!silent) { setLoading(true); setLoadError(''); }
    return get(`/lead/kanban?club=${currentClub.id}`)
      .then(b => { setBoard(b); return b; })
      .catch(e => {
        if (silent) setToast('Could not refresh the board. The list may be out of date.');
        else setLoadError(e.message || 'Failed to load');
        return null;
      })
      .finally(() => { if (!silent) setLoading(false); });
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Restore the remembered card for this club (the card that was NEXT when the last one was saved, else the last one).
  useEffect(() => {
    if (!currentClub?.id) return;
    try {
      const saved = JSON.parse(readStore(lastKey(currentClub.id), 'null'));
      setResumeId(saved ? (saved.nextId || saved.cardId) : null);
    } catch { setResumeId(null); }
  }, [currentClub?.id]);

  // Coming back to the board (a new visit, or after leaving the page): scroll straight to the remembered card,
  // once per club, instead of landing at the top of a column with hundreds of cards.
  useEffect(() => {
    if (!board || !resumeId || !currentClub?.id || scrolledToResume.current === currentClub.id) return;
    if (Object.values(board.stages || {}).some(list => list.some(c => c.id === resumeId))) {
      scrolledToResume.current = currentClub.id;
      scrollToCard(resumeId);
    }
  }, [board, resumeId, currentClub?.id]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function remember(stageKey, cardId, nextId) {
    setResumeId(nextId || cardId);
    if (currentClub?.id) writeStore(lastKey(currentClub.id), JSON.stringify({ stageKey, cardId, nextId: nextId || null, at: Date.now() }));
  }

  function openCard(card, stageKey) {
    queueRef.current = { stageKey, ids: (board?.stages?.[stageKey] || []).map(c => c.id) };
    setOutcomeTarget(card);
  }

  // The first card after `currentId` (in the order the column was showing) that is still in that column.
  function nextCardAfter(currentId, data) {
    const q = queueRef.current;
    if (!q || !data) return null;
    const idx = q.ids.indexOf(currentId);
    if (idx < 0) return null;
    const cards = data.stages?.[q.stageKey] || [];
    const byId = new Map(cards.map(c => [c.id, c]));
    for (let i = idx + 1; i < q.ids.length; i++) if (byId.has(q.ids[i])) return byId.get(q.ids[i]);
    return null;
  }

  function openNext(next, data) {
    queueRef.current = { stageKey: queueRef.current.stageKey, ids: (data.stages?.[queueRef.current.stageKey] || []).map(c => c.id) };
    setOutcomeTarget(next);
    scrollToCard(next.id);
  }

  async function handleSaved(outcome) {
    const card = outcomeTarget;
    const stageKey = queueRef.current?.stageKey;
    setOutcomeTarget(null);
    const fresh = await load(true);
    if (!card || !stageKey) return;
    const next = nextCardAfter(card.id, fresh);
    remember(stageKey, card.id, next?.id);
    if (!outcome) return;                       // a note does not move the card or the queue
    if (autoNext && next) openNext(next, fresh);
    else if (autoNext) setToast('That was the last card in this column.');
    else if (next) scrollToCard(next.id);
  }

  function handleSkip() {
    const card = outcomeTarget;
    const stageKey = queueRef.current?.stageKey;
    const next = nextCardAfter(card?.id, board);
    if (card && stageKey) remember(stageKey, card.id, next?.id);
    if (next) openNext(next, board);
    else { setOutcomeTarget(null); setToast('That was the last card in this column.'); }
  }

  function toggleAutoNext() {
    const v = !autoNext;
    setAutoNext(v);
    writeStore(AUTO_NEXT_KEY, v ? 'on' : 'off');
  }

  function queueLabel() {
    const q = queueRef.current;
    if (!q || !outcomeTarget) return null;
    const cards = board?.stages?.[q.stageKey] || [];
    const pos = cards.findIndex(c => c.id === outcomeTarget.id) + 1;
    return pos > 0 ? `${STAGE_META[q.stageKey]?.label || q.stageKey} · card ${pos} of ${cards.length}` : null;
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (loadError) return (
    <div className="empty-state">
      <p>Couldn't load the pipeline: {loadError}</p>
    </div>
  );
  if (!board) return <div className="empty-state"><p>No data.</p></div>;

  const total = Object.values(board.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="module-page kanban-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>📋</span>
          <h1 className="module-title">Lead Engine Pipeline</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {resumeId && board && Object.values(board.stages || {}).some(list => list.some(c => c.id === resumeId)) && (
            <button className="btn btn-ghost btn-sm" onClick={() => scrollToCard(resumeId)}
              title="Scroll back to the card you were on when you last logged a contact">
              ↩ Resume where I left off
            </button>
          )}
          <label style={{ fontSize: 12, color: '#aaa', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
            title="After you save a contact, the next card in that column opens automatically">
            <input type="checkbox" checked={autoNext} onChange={toggleAutoNext} />
            Open the next card after saving
          </label>
          <div style={{ fontSize: 12.5, color: '#888' }}>{total} active</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <LeadGoalStat />
        <FollowUpKPI get={get} />
        <ContactsKPI get={get} />
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
        {COLUMN_ORDER.map(stageKey => (
          <Column
            key={stageKey}
            stageKey={stageKey}
            cards={board.stages?.[stageKey] || []}
            onLogOutcome={openCard}
            onViewHistory={setHistoryTarget}
            resumeId={resumeId}
          />
        ))}
      </div>

      {outcomeTarget && (
        <LeadLogContactModal
          key={outcomeTarget.id}
          prospect={outcomeTarget}
          clubName={currentClub?.name}
          queueInfo={queueLabel()}
          onSkip={handleSkip}
          onClose={() => { remember(queueRef.current?.stageKey, outcomeTarget.id, null); setOutcomeTarget(null); }}
          onSaved={handleSaved}
        />
      )}

      {historyTarget && (
        <HistorySidePanel card={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1f2937', color: '#e5e7eb', padding: '9px 16px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 2000 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
